// components/Admin/Packaging/PackagingDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  QrCode,
  FileText,
  Printer,
  Download,
  Search,
  Calendar,
  ShoppingBag,
  Plus,
  RefreshCw,
  Eye,
  X,
  Loader,
  AlertCircle,
  CheckCircle,
  Clock,
  Truck,
  Home,
  Copy,
  Layers,
  Grid,
  List,
  Check,
  DollarSign,
  TrendingUp,
  Users,
  Sparkles,
  Boxes
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import OrderPackingSlip from '../components/Admin/Order-Management/OrderPackingSlip';
import AdminAdHocPackingSlip from '../components/Admin/Order-Management/AdminAdHocPackingSlip';
import ArtisanAdHocPackingSlip from '../components/Admin/Order-Management/ArtisanAdHocPackingSlip';
import TantikaLogo from '../Assets/TantikaLogo.png';

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const inputClass = "w-full px-4 py-2.5 border border-cream-300 bg-white rounded-xl focus:ring-2 focus:ring-clay-500 focus:border-transparent outline-none transition-all placeholder:text-cocoa-400 text-cocoa-800";
const primaryBtn = "inline-flex items-center gap-2 bg-gradient-to-r from-clay-500 to-saffron-500 text-white px-5 py-2.5 rounded-full font-semibold text-sm shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0";
const secondaryBtn = "inline-flex items-center gap-2 border-2 border-cream-300 text-cocoa-700 px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-cream-50 transition-all duration-300";

// ========== QR CODE GENERATOR COMPONENT ==========
const QRCodeGenerator = ({ onClose }) => {
  const [qrText, setQrText] = useState('https://tantikacrafts.netlify.app');
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qrError, setQrError] = useState(false);
  const [qrSize, setQrSize] = useState(200);
  const [qrColor, setQrColor] = useState('#934026');
  const [qrBgColor, setQrBgColor] = useState('#ffffff');
  const [generatedQrs, setGeneratedQrs] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('website');
  const qrRef = useRef(null);

  const presets = {
    website: { text: 'https://tantikacrafts.netlify.app', label: 'Website' },
    instagram: { text: 'https://instagram.com/tantikacrafts', label: 'Instagram' },
    facebook: { text: 'https://facebook.com/tantikacrafts', label: 'Facebook' },
    youtube: { text: 'https://youtube.com/@tantikacrafts', label: 'YouTube' },
    whatsapp: { text: 'https://wa.me/919999999999', label: 'WhatsApp' }
  };

  useEffect(() => {
    generateQRCode();
  }, [qrText, qrSize, qrColor, qrBgColor]);

  const generateQRCode = async () => {
    if (!qrText || qrText.trim() === '') {
      setQrError(true);
      return;
    }

    setLoading(true);
    setQrError(false);

    try {
      const apiUrl = `https://quickchart.io/qr?text=${encodeURIComponent(qrText)}&size=${qrSize}&margin=4&dark=${qrColor.replace('#', '')}&light=${qrBgColor.replace('#', '')}&format=png`;

      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = apiUrl;
      });

      setQrCodeUrl(apiUrl);
    } catch (error) {
      console.error('QR Code generation error:', error);
      setQrError(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePresetChange = (presetKey) => {
    setSelectedPreset(presetKey);
    setQrText(presets[presetKey].text);
  };

  const handleDownloadQR = async () => {
    if (!qrRef.current) return;

    try {
      const canvas = await html2canvas(qrRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false
      });

      const link = document.createElement('a');
      link.download = `qr-code-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Download QR error:', error);
      alert('Failed to download QR code');
    }
  };

  const handleCopyText = () => {
    navigator.clipboard.writeText(qrText);
    alert('QR text copied to clipboard!');
  };

  const handleSaveQR = () => {
    if (qrCodeUrl && !qrError) {
      const newQr = {
        id: Date.now(),
        text: qrText,
        url: qrCodeUrl,
        size: qrSize,
        createdAt: new Date().toISOString(),
        label: qrText.substring(0, 30) + (qrText.length > 30 ? '...' : '')
      };
      setGeneratedQrs([newQr, ...generatedQrs]);
      alert('QR code saved to history!');
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto animate-fadeInUp">
      <div className="flex items-center gap-3 mb-8">
        <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-clay-500 to-saffron-500 text-white shadow-warm">
          <QrCode className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-xl font-display font-bold text-cocoa-900">QR Code Generator</h3>
          <p className="text-sm text-cocoa-500">Generate custom QR codes for your business</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Panel - Controls */}
        <div className="space-y-5 bg-white rounded-2xl border border-cream-200 shadow-sm p-6">
          <div>
            <label className="block text-sm font-semibold text-cocoa-800 mb-2">
              Preset Links
            </label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(presets).map(([key, preset]) => (
                <button
                  key={key}
                  onClick={() => handlePresetChange(key)}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                    selectedPreset === key
                      ? 'bg-gradient-to-r from-clay-500 to-saffron-500 text-white shadow-warm'
                      : 'bg-cream-100 text-cocoa-700 hover:bg-cream-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-cocoa-800 mb-2">
              QR Content URL / Text
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={qrText}
                onChange={(e) => setQrText(e.target.value)}
                placeholder="Enter URL or text for QR code"
                className={inputClass}
              />
              <button
                onClick={handleCopyText}
                className="px-3 py-2 bg-cream-100 text-cocoa-600 rounded-xl hover:bg-cream-200 transition-colors shrink-0"
                title="Copy text"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-cocoa-800 mb-2">
              QR Code Size: <span className="text-clay-600 font-bold">{qrSize}px</span>
            </label>
            <input
              type="range"
              min="100"
              max="500"
              value={qrSize}
              onChange={(e) => setQrSize(parseInt(e.target.value))}
              className="w-full accent-clay-600"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-cocoa-800 mb-2">
                Foreground Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={qrColor}
                  onChange={(e) => setQrColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-cream-300 cursor-pointer"
                />
                <span className="text-xs text-cocoa-400 font-mono">{qrColor}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-cocoa-800 mb-2">
                Background Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={qrBgColor}
                  onChange={(e) => setQrBgColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-cream-300 cursor-pointer"
                />
                <span className="text-xs text-cocoa-400 font-mono">{qrBgColor}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={generateQRCode}
              className={`${primaryBtn} flex-1 justify-center`}
            >
              <RefreshCw className="w-4 h-4" />
              Generate
            </button>
            <button
              onClick={handleSaveQR}
              className="inline-flex items-center gap-2 bg-white border-2 border-green-200 text-green-700 px-4 py-2.5 rounded-full font-semibold text-sm hover:bg-green-50 transition-all duration-300"
            >
              <Check className="w-4 h-4" />
              Save
            </button>
          </div>
        </div>

        {/* Right Panel - QR Code Display */}
        <div className="flex flex-col items-center">
          <div
            ref={qrRef}
            className="bg-white p-6 rounded-2xl shadow-warm border border-cream-200 flex flex-col items-center"
          >
            {loading ? (
              <div className="w-64 h-64 bg-cream-100 rounded-xl flex items-center justify-center">
                <Loader className="w-8 h-8 animate-spin text-clay-600" />
              </div>
            ) : qrError ? (
              <div className="w-64 h-64 bg-cream-100 rounded-xl flex flex-col items-center justify-center">
                <AlertCircle className="w-12 h-12 text-red-500" />
                <p className="text-sm text-red-500 mt-2">Invalid QR content</p>
              </div>
            ) : qrCodeUrl ? (
              <img
                src={qrCodeUrl}
                alt="QR Code"
                className="w-64 h-64 object-contain rounded-xl"
                crossOrigin="anonymous"
              />
            ) : (
              <div className="w-64 h-64 bg-cream-100 rounded-xl flex items-center justify-center">
                <QrCode className="w-12 h-12 text-cocoa-300" />
              </div>
            )}
            <p className="mt-4 text-sm text-cocoa-500 text-center break-all max-w-xs">
              {qrText || 'Enter content above'}
            </p>
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleDownloadQR}
              disabled={!qrCodeUrl || qrError}
              className={`${primaryBtn} disabled:opacity-50`}
            >
              <Download className="w-4 h-4" />
              Download PNG
            </button>
            <button
              onClick={onClose}
              className={secondaryBtn}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* QR History */}
      {generatedQrs.length > 0 && (
        <div className="mt-8">
          <h4 className="text-sm font-display font-bold text-cocoa-900 mb-3">Saved QR Codes</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {generatedQrs.slice(0, 6).map((qr) => (
              <div key={qr.id} className="bg-white rounded-2xl border border-cream-200 p-3 flex items-center gap-3 shadow-sm hover:shadow-warm transition-all">
                <img src={qr.url} alt="QR" className="w-12 h-12 object-contain" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-cocoa-800 truncate">{qr.label}</p>
                  <p className="text-xs text-cocoa-400">{new Date(qr.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== INVOICE GENERATOR COMPONENT ==========
const InvoiceGenerator = ({ order, onClose }) => {
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const invoiceRef = useRef(null);

  if (!order) return null;

  const getCustomerName = () => {
    if (order.customer?.name) return order.customer.name;
    return order.customerName || 'Customer';
  };

  const getCustomerEmail = () => {
    if (order.customer?.email) return order.customer.email;
    return order.customerEmail || '';
  };

  const getCustomerPhone = () => {
    if (order.customer?.phone) return order.customer.phone;
    return order.customerPhone || '';
  };

  const getCustomerAddress = () => {
    if (order.customer?.shippingAddress) {
      const addr = order.customer.shippingAddress;
      if (typeof addr === 'string') return addr;
      const parts = [
        addr.street || '',
        addr.city || '',
        addr.state || '',
        addr.postalCode || '',
        addr.country || 'India'
      ].filter(Boolean);
      return parts.join(', ');
    }
    return order.customerAddress || '';
  };

  const getSubtotal = () => order.subtotal || 0;
  const getTax = () => order.tax || 0;
  const getShippingCost = () => order.shipping?.shippingCost || order.shippingCost || 0;
  const getTotal = () => {
    if (order.total !== undefined && order.total !== null) return order.total;
    return getSubtotal() + getTax() + getShippingCost();
  };
  const getPaymentMethod = () => order.payment?.method || order.paymentMethod || 'N/A';
  const getPaymentStatus = () => order.payment?.status || order.paymentStatus || 'N/A';

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const handleGeneratePDF = async () => {
    if (!invoiceRef.current) return;

    try {
      setGeneratingPDF(true);
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`invoice-${order.orderNumber}.pdf`);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const subtotal = getSubtotal();
  const tax = getTax();
  const shippingCost = getShippingCost();
  const total = getTotal();

  return (
    <div className="fixed inset-0 bg-cocoa-950/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-4 z-50">
      <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl animate-slideUp">
        <div className="sticky top-0 z-20 bg-gradient-to-br from-cream-50 via-white to-saffron-50 border-b border-cream-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-cocoa-600 to-cocoa-800 text-white shadow-warm">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-display font-bold text-cocoa-900">Invoice</h3>
              <p className="text-sm text-cocoa-500">Order #{order.orderNumber}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className={secondaryBtn}
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={handleGeneratePDF}
              disabled={generatingPDF}
              className={primaryBtn}
            >
              {generatingPDF ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download PDF
            </button>
            <button onClick={onClose} className="p-2 hover:bg-cream-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-cocoa-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-gradient-to-br from-cream-100 via-cream-50 to-saffron-50 scrollbar-thin">
          <div
            ref={invoiceRef}
            id="print-area"
            className="bg-white rounded-2xl shadow-warm p-8 max-w-3xl mx-auto print:shadow-none print:p-6"
          >
            <div className="space-y-6">
              <div className="flex justify-between items-start border-b-2 border-cocoa-200 pb-4">
                <div className="flex items-center gap-3">
                  <img src={TantikaLogo} alt="তন্তিকা" className="h-16 w-auto object-contain" />
                  <div>
                    <h1 className="text-2xl font-display font-bold bg-gradient-to-r from-clay-600 to-saffron-600 bg-clip-text text-transparent">তন্তিকা</h1>
                    <p className="text-sm text-cocoa-500">Handcrafted with ❤️</p>
                    <p className="text-sm text-cocoa-500">https://tantikacrafts.netlify.app</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-display font-bold tracking-widest text-cocoa-800">INVOICE</div>
                  <div className="text-sm text-cocoa-600 mt-1 font-semibold">#{order.orderNumber}</div>
                  <div className="text-xs text-cocoa-400">Date: {formatDate(new Date())}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-display font-bold text-cocoa-800 mb-2">Bill To:</h4>
                  <div className="bg-cream-50 border border-cream-100 p-3 rounded-xl space-y-1 text-sm">
                    <p className="font-semibold text-cocoa-900">{getCustomerName()}</p>
                    <p className="text-cocoa-600">{getCustomerEmail()}</p>
                    <p className="text-cocoa-600">{getCustomerPhone()}</p>
                    <p className="text-cocoa-600">{getCustomerAddress()}</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-display font-bold text-cocoa-800 mb-2">Invoice Details:</h4>
                  <div className="bg-cream-50 border border-cream-100 p-3 rounded-xl space-y-1 text-sm">
                    <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Order Date:</span> {formatDate(order.createdAt)}</p>
                    <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Payment Method:</span> {getPaymentMethod()}</p>
                    <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Payment Status:</span> {getPaymentStatus()}</p>
                  </div>
                </div>
              </div>

              <div className="border border-cream-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-cream-50">
                    <tr>
                      <th className="text-left px-3 py-2.5 font-semibold text-cocoa-600">#</th>
                      <th className="text-left px-3 py-2.5 font-semibold text-cocoa-600">Item</th>
                      <th className="text-center px-3 py-2.5 font-semibold text-cocoa-600">Qty</th>
                      <th className="text-right px-3 py-2.5 font-semibold text-cocoa-600">Price</th>
                      <th className="text-right px-3 py-2.5 font-semibold text-cocoa-600">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.items || []).map((item, index) => (
                      <tr key={index} className="border-t border-cream-100">
                        <td className="px-3 py-2.5 text-cocoa-400">{index + 1}</td>
                        <td className="px-3 py-2.5">
                          <div>
                            <div className="font-medium text-cocoa-900">{item.name}</div>
                            {item.sku && <div className="text-xs text-cocoa-400">SKU: {item.sku}</div>}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-center text-cocoa-800">{item.quantity}</td>
                        <td className="px-3 py-2.5 text-right text-cocoa-800">{formatCurrency(item.price)}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-cocoa-900">
                          {formatCurrency((item.price || 0) * (item.quantity || 1))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-cream-50 border-t border-cream-200">
                    <tr>
                      <td colSpan="4" className="px-3 py-2 text-right font-semibold text-cocoa-700">Subtotal</td>
                      <td className="px-3 py-2 text-right font-semibold text-cocoa-900">{formatCurrency(subtotal)}</td>
                    </tr>
                    <tr>
                      <td colSpan="4" className="px-3 py-2 text-right text-sm text-cocoa-500">Tax</td>
                      <td className="px-3 py-2 text-right text-sm text-cocoa-700">{formatCurrency(tax)}</td>
                    </tr>
                    <tr>
                      <td colSpan="4" className="px-3 py-2 text-right text-sm text-cocoa-500">Shipping</td>
                      <td className="px-3 py-2 text-right text-sm text-cocoa-700">{formatCurrency(shippingCost)}</td>
                    </tr>
                    <tr className="border-t-2 border-cocoa-500">
                      <td colSpan="4" className="px-3 py-2.5 text-right font-bold text-cocoa-800">Total</td>
                      <td className="px-3 py-2.5 text-right font-bold text-cocoa-800">{formatCurrency(total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="text-center text-xs text-cocoa-400 border-t border-cream-200 pt-4">
                <p>Thank you for shopping with তন্তিকা!</p>
                <p className="mt-1">This is a system generated invoice for order #{order.orderNumber}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== MAIN DASHBOARD COMPONENT ==========
const PackagingDashboard = ({ onClose }) => {
  const navigate = useNavigate();
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      const userData = JSON.parse(localStorage.getItem('tantika_user') || '{}');
      navigate(userData.role === 'artisan' ? '/artisan/dashboard' : '/admin/Addashboard');
    }
  };

  // State
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showPackingSlip, setShowPackingSlip] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [userRole, setUserRole] = useState('admin');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0
  });

  // Tabs configuration
  const tabs = [
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
    { id: 'adhoc', label: 'Ad-Hoc Slip', icon: Plus },
    { id: 'qr', label: 'QR Generator', icon: QrCode },
    { id: 'stats', label: 'Statistics', icon: TrendingUp }
  ];

  // ========== Helper functions for order data extraction ==========
  const getOrderTotal = (order) => {
    if (order.total !== undefined && order.total !== null) return order.total;
    const subtotal = order.subtotal || 0;
    const tax = order.tax || 0;
    const shipping = order.shipping?.shippingCost || order.shippingCost || 0;
    return subtotal + tax + shipping;
  };

  const getOrderCustomerName = (order) => {
    return order.customer?.name || order.customerName || 'N/A';
  };

  const getOrderCustomerPhone = (order) => {
    return order.customer?.phone || order.customerPhone || '';
  };

  const getOrderCustomerEmail = (order) => {
    return order.customer?.email || order.customerEmail || '';
  };

  // ========== Get user role helper ==========
  const getUserRole = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('tantika_user') || '{}');
      return userData.role || 'admin';
    } catch {
      return 'admin';
    }
  };

  // ========== Render Ad-Hoc Slip based on role ==========
  const renderAdHocSlip = () => {
    const role = getUserRole();

    if (role === 'admin' || role === 'superAdmin') {
      return <AdminAdHocPackingSlip onClose={() => setActiveTab('orders')} />;
    } else if (role === 'artisan') {
      return <ArtisanAdHocPackingSlip onClose={() => setActiveTab('orders')} />;
    } else {
      return <AdminAdHocPackingSlip onClose={() => setActiveTab('orders')} />;
    }
  };

  // Fetch orders based on user role
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('tantika_token');
      if (!token) {
        console.error('No auth token found');
        return;
      }

      const userData = JSON.parse(localStorage.getItem('tantika_user') || '{}');
      const role = userData.role || 'admin';
      setUserRole(role);

      let endpoint;
      if (role === 'admin' || role === 'superAdmin') {
        endpoint = `${API_BASE_URL}/orders/admin/all?page=1&limit=100`;
      } else if (role === 'artisan') {
        endpoint = `${API_BASE_URL}/artisan/orders?page=1&limit=100`;
      } else {
        endpoint = `${API_BASE_URL}/orders/my-orders`;
      }

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        const orderList = data.data?.orders || data.data || [];

        const transformedOrders = orderList.map(order => ({
          ...order,
          _id: order._id,
          orderNumber: order.orderNumber,
          status: order.status,
          createdAt: order.createdAt,
          items: order.items || [],
          customer: order.customer || null,
          customerName: getOrderCustomerName(order),
          customerPhone: getOrderCustomerPhone(order),
          customerEmail: getOrderCustomerEmail(order),
          payment: order.payment || null,
          paymentMethod: order.payment?.method || order.paymentMethod || 'N/A',
          paymentStatus: order.payment?.status || order.paymentStatus || 'N/A',
          shipping: order.shipping || null,
          shippingCost: order.shipping?.shippingCost || order.shippingCost || 0,
          subtotal: order.subtotal || 0,
          tax: order.tax || 0,
          total: getOrderTotal(order)
        }));

        setOrders(transformedOrders);

        const statsData = {
          total: transformedOrders.length,
          pending: transformedOrders.filter(o => o.status === 'pending').length,
          processing: transformedOrders.filter(o => ['confirmed', 'processing', 'ready_to_ship'].includes(o.status)).length,
          shipped: transformedOrders.filter(o => ['shipped', 'out_for_delivery'].includes(o.status)).length,
          delivered: transformedOrders.filter(o => o.status === 'delivered').length,
          cancelled: transformedOrders.filter(o => o.status === 'cancelled').length
        };
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (order.customer?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          order.customerName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesDate = !dateFilter || new Date(order.createdAt).toDateString() === new Date(dateFilter).toDateString();
    return matchesSearch && matchesStatus && matchesDate;
  });

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-saffron-100 text-saffron-700',
      'contacted': 'bg-orange-100 text-orange-800',
      'confirmed': 'bg-clay-100 text-clay-700',
      'processing': 'bg-cocoa-100 text-cocoa-700',
      'ready_to_ship': 'bg-cyan-100 text-cyan-800',
      'shipped': 'bg-blue-100 text-blue-800',
      'out_for_delivery': 'bg-teal-100 text-teal-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending',
      'contacted': 'Contacted',
      'confirmed': 'Confirmed',
      'processing': 'Processing',
      'ready_to_ship': 'Ready to Ship',
      'shipped': 'Shipped',
      'out_for_delivery': 'Out for Delivery',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status || 'Unknown';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'processing': return <Loader className="w-4 h-4" />;
      case 'shipped': return <Truck className="w-4 h-4" />;
      case 'delivered': return <Home className="w-4 h-4" />;
      case 'cancelled': return <X className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Handle generate packing slip
  const handleGeneratePackingSlip = (order) => {
    setSelectedOrder(order);
    setShowPackingSlip(true);
  };

  // Handle generate invoice
  const handleGenerateInvoice = (order) => {
    setSelectedOrder(order);
    setShowInvoice(true);
  };

  // Render orders list
  const renderOrdersList = () => (
    <div className="space-y-5 animate-fadeInUp">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white p-4 rounded-2xl border border-cream-200 shadow-sm text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cocoa-500 to-cocoa-400" />
          <p className="text-2xl font-bold text-cocoa-900">{stats.total}</p>
          <p className="text-xs text-cocoa-500 mt-0.5">Total</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-saffron-200 shadow-sm text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-saffron-400 to-saffron-500" />
          <p className="text-2xl font-bold text-saffron-700">{stats.pending}</p>
          <p className="text-xs text-saffron-600 mt-0.5">Pending</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-cocoa-200 shadow-sm text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cocoa-400 to-cocoa-500" />
          <p className="text-2xl font-bold text-cocoa-700">{stats.processing}</p>
          <p className="text-xs text-cocoa-600 mt-0.5">Processing</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-blue-200 shadow-sm text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-blue-500" />
          <p className="text-2xl font-bold text-blue-700">{stats.shipped}</p>
          <p className="text-xs text-blue-600 mt-0.5">Shipped</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-green-200 shadow-sm text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-green-500" />
          <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
          <p className="text-xs text-green-600 mt-0.5">Delivered</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-red-200 shadow-sm text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-400 to-red-500" />
          <p className="text-2xl font-bold text-red-700">{stats.cancelled}</p>
          <p className="text-xs text-red-600 mt-0.5">Cancelled</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center justify-between bg-white rounded-2xl border border-cream-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cocoa-400" />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2.5 border border-cream-300 bg-white rounded-xl focus:ring-2 focus:ring-clay-500 focus:border-transparent outline-none transition-all w-64 placeholder:text-cocoa-400 text-cocoa-800"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={inputClass}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="processing">Processing</option>
            <option value="ready_to_ship">Ready to Ship</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cocoa-400" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="pl-10 pr-4 py-2.5 border border-cream-300 bg-white rounded-xl focus:ring-2 focus:ring-clay-500 focus:border-transparent outline-none transition-all text-cocoa-800"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('list')}
            className={`p-2.5 rounded-xl transition-all duration-300 ${viewMode === 'list' ? 'bg-gradient-to-r from-clay-500 to-saffron-500 text-white shadow-warm' : 'bg-cream-100 text-cocoa-500 hover:bg-cream-200'}`}
            title="List view"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2.5 rounded-xl transition-all duration-300 ${viewMode === 'grid' ? 'bg-gradient-to-r from-clay-500 to-saffron-500 text-white shadow-warm' : 'bg-cream-100 text-cocoa-500 hover:bg-cream-200'}`}
            title="Grid view"
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={fetchOrders}
            className="p-2.5 bg-cream-100 text-cocoa-500 rounded-xl hover:bg-cream-200 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-cream-200">
          <Loader className="w-8 h-8 animate-spin text-clay-600" />
          <p className="text-cocoa-500 text-sm mt-3">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-cream-200">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cream-100 mb-4">
            <Package className="w-8 h-8 text-cocoa-400" />
          </div>
          <p className="text-cocoa-500 font-medium">No orders found</p>
          <p className="text-cocoa-400 text-sm mt-1">Try adjusting your search or filters</p>
        </div>
      ) : viewMode === 'list' ? (
        <div className="bg-white rounded-2xl border border-cream-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-cream-50 border-b border-cream-200">
              <tr>
                <th className="text-left px-4 py-3.5 text-xs font-semibold text-cocoa-500 uppercase tracking-wider">Order</th>
                <th className="text-left px-4 py-3.5 text-xs font-semibold text-cocoa-500 uppercase tracking-wider">Customer</th>
                <th className="text-left px-4 py-3.5 text-xs font-semibold text-cocoa-500 uppercase tracking-wider">Date</th>
                <th className="text-left px-4 py-3.5 text-xs font-semibold text-cocoa-500 uppercase tracking-wider">Total</th>
                <th className="text-left px-4 py-3.5 text-xs font-semibold text-cocoa-500 uppercase tracking-wider">Status</th>
                <th className="text-center px-4 py-3.5 text-xs font-semibold text-cocoa-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order._id} className="border-b border-cream-100 hover:bg-cream-50 transition-colors">
                  <td className="px-4 py-3.5">
                    <div className="font-semibold text-cocoa-900">{order.orderNumber}</div>
                    <div className="text-xs text-cocoa-400">{order.items?.length || 0} items</div>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="font-medium text-cocoa-800">{order.customer?.name || order.customerName || 'N/A'}</div>
                    <div className="text-xs text-cocoa-400">{order.customer?.phone || order.customerPhone || ''}</div>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-cocoa-600">{formatDate(order.createdAt)}</td>
                  <td className="px-4 py-3.5 font-bold text-cocoa-900">{formatCurrency(order.total)}</td>
                  <td className="px-4 py-3.5">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 w-fit ${getStatusColor(order.status)}`}>
                      {getStatusIcon(order.status)}
                      {getStatusLabel(order.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleGeneratePackingSlip(order)}
                        className="p-2 text-clay-600 hover:bg-clay-50 rounded-xl transition-colors"
                        title="Generate Packing Slip"
                      >
                        <Package className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleGenerateInvoice(order)}
                        className="p-2 text-cocoa-600 hover:bg-cocoa-50 rounded-xl transition-colors"
                        title="Generate Invoice"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        className="p-2 text-cocoa-400 hover:bg-cream-100 rounded-xl transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOrders.map((order) => (
            <div key={order._id} className="bg-white rounded-2xl border border-cream-200 p-5 hover:shadow-warm transition-all duration-300 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-clay-500 to-saffron-500" />
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-bold text-cocoa-900">{order.orderNumber}</p>
                  <p className="text-sm text-cocoa-500">{order.customer?.name || order.customerName || 'N/A'}</p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                  {getStatusLabel(order.status)}
                </span>
              </div>
              <div className="space-y-1.5 text-sm">
                <p className="text-cocoa-600 flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5 text-cocoa-400" />
                  {formatDate(order.createdAt)}
                </p>
                <p className="text-cocoa-600 flex items-center gap-2">
                  <DollarSign className="w-3.5 h-3.5 text-cocoa-400" />
                  <span className="font-bold text-cocoa-900">{formatCurrency(order.total)}</span>
                </p>
                <p className="text-cocoa-600 flex items-center gap-2">
                  <Boxes className="w-3.5 h-3.5 text-cocoa-400" />
                  {order.items?.length || 0} items
                </p>
              </div>
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-cream-100">
                <button
                  onClick={() => handleGeneratePackingSlip(order)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-clay-50 text-clay-700 rounded-full text-xs font-semibold hover:bg-clay-100 transition-colors"
                >
                  <Package className="w-3.5 h-3.5" />
                  Slip
                </button>
                <button
                  onClick={() => handleGenerateInvoice(order)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cocoa-50 text-cocoa-700 rounded-full text-xs font-semibold hover:bg-cocoa-100 transition-colors"
                >
                  <FileText className="w-3.5 h-3.5" />
                  Invoice
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render statistics
  const renderStats = () => (
    <div className="space-y-6 animate-fadeInUp">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-cream-200 relative overflow-hidden hover:shadow-warm transition-all">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-clay-500 to-clay-600" />
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-clay-100 to-clay-200 text-clay-700">
              <ShoppingBag className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-cocoa-500">Total Orders</p>
              <p className="text-2xl font-bold text-cocoa-900">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-cream-200 relative overflow-hidden hover:shadow-warm transition-all">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-green-600" />
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-green-100 to-green-200 text-green-700">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-cocoa-500">Total Revenue</p>
              <p className="text-2xl font-bold text-cocoa-900">
                {formatCurrency(orders.reduce((sum, o) => sum + (o.total || 0), 0))}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-cream-200 relative overflow-hidden hover:shadow-warm transition-all">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-blue-600" />
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-cocoa-500">Unique Customers</p>
              <p className="text-2xl font-bold text-cocoa-900">
                {new Set(orders.map(o => o.customer?.email || o.customerEmail)).size}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-cream-200 relative overflow-hidden hover:shadow-warm transition-all">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-saffron-500 to-saffron-600" />
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-saffron-100 to-saffron-200 text-saffron-700">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-cocoa-500">Completion Rate</p>
              <p className="text-2xl font-bold text-cocoa-900">
                {stats.total > 0 ? Math.round((stats.delivered / stats.total) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-cream-200">
        <h4 className="font-display font-bold text-cocoa-900 mb-6">Order Status Distribution</h4>
        <div className="space-y-4">
          {Object.entries(stats).map(([key, value]) => {
            if (key === 'total') return null;
            const percentage = stats.total > 0 ? Math.round((value / stats.total) * 100) : 0;
            const colors = {
              pending: 'from-saffron-400 to-saffron-500',
              processing: 'from-cocoa-400 to-cocoa-500',
              shipped: 'from-blue-400 to-blue-500',
              delivered: 'from-green-400 to-green-500',
              cancelled: 'from-red-400 to-red-500'
            };
            const textColors = {
              pending: 'text-saffron-600',
              processing: 'text-cocoa-600',
              shipped: 'text-blue-600',
              delivered: 'text-green-600',
              cancelled: 'text-red-600'
            };
            return (
              <div key={key}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-semibold text-cocoa-700 capitalize">{key}</span>
                  <span className={`font-semibold ${textColors[key] || 'text-cocoa-500'}`}>{value} ({percentage}%)</span>
                </div>
                <div className="w-full bg-cream-100 rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`bg-gradient-to-r ${colors[key] || 'bg-gray-500'} h-2.5 rounded-full transition-all duration-700`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'orders':
        return renderOrdersList();
      case 'qr':
        return <QRCodeGenerator onClose={() => setActiveTab('orders')} />;
      case 'adhoc':
        return renderAdHocSlip();
      case 'stats':
        return renderStats();
      default:
        return renderOrdersList();
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-cocoa-950/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-4 z-50">
        <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl animate-slideUp">
          {/* Header */}
          <div className="sticky top-0 z-20 bg-gradient-to-br from-cream-50 via-white to-saffron-50 border-b border-cream-200 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-clay-500 to-saffron-500 text-white shadow-warm">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-display font-bold text-cocoa-900">
                  Packaging Dashboard
                </h3>
                <p className="text-sm text-cocoa-500 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-saffron-500" />
                  {userRole === 'admin' ? 'Admin' : userRole === 'artisan' ? 'Artisan' : 'User'} • {orders.length} orders loaded
                </p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-cream-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-cocoa-500" />
            </button>
          </div>

          {/* Tabs */}
          <div className="bg-white px-4 md:px-6 border-b border-cream-200 flex gap-1 overflow-x-auto scrollbar-none">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-sm font-semibold transition-all duration-300 border-b-2 flex items-center gap-2 whitespace-nowrap -mb-px ${
                    isActive
                      ? 'border-clay-500 text-clay-700'
                      : 'border-transparent text-cocoa-400 hover:text-cocoa-700 hover:bg-cream-50'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-clay-600' : ''}`} />
                  {tab.label}
                  {tab.id === 'orders' && orders.length > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${isActive ? 'bg-clay-100 text-clay-700' : 'bg-cream-100 text-cocoa-500'}`}>
                      {orders.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gradient-to-br from-cream-100 via-cream-50 to-saffron-50 scrollbar-thin">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showPackingSlip && selectedOrder && (
        <OrderPackingSlip
          order={selectedOrder}
          onClose={() => {
            setShowPackingSlip(false);
            setSelectedOrder(null);
          }}
        />
      )}

      {showInvoice && selectedOrder && (
        <InvoiceGenerator
          order={selectedOrder}
          onClose={() => {
            setShowInvoice(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </>
  );
};

export default PackagingDashboard;
